/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Wed Dec 20 2023
 **/
import { useCallback, useEffect } from "react";
import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { timer } from "rxjs";

import { useVoiceCall } from "@/frameworks/core/API";
import { useChatRoomActions, useChatRoomListActions, useSpaceChannelState } from "@/frameworks/core/chat";
import { EventID, GROUP_POPOVER_ID, ModalID, SidebarID, ZIndex } from "@/frameworks/core/commons";
import { ChatAlarmSetting, ChatAlarmSettingMenu, ChatVote, ShowChatMember } from "@/frameworks/core/components";
import {
	useConfirm,
	useMiniChatPopupManager,
	useNotificationConfig,
	usePopover,
	useSidebarActions
} from "@/frameworks/core/hooks";
import { usePersistUser } from "@/frameworks/core/persistence";
import {
	Divider,
	Icon,
	Menu,
	Row,
	Typography,
	TypographyTooltip,
	useModal,
	useToast
} from "@/frameworks/designStuff/components";
import { Configure } from "@/frameworks/libraries";
import { Maybe, SwitchCase } from "@/frameworks/libraries/components";
import { Properties } from "@/frameworks/libraries/properties";
import { EventHandler } from "@rfice/eventhandler";
import { _P, _T } from "@rfice/language";

import * as S from "../../../common/components/emotion";
import { CreateSpaceChannel } from "../CreateSpaceChannel";
interface SpaceChannelOptionInterface {
	roomType: "general" | "public" | "private";
	roomID: string;
	notificationType: WebServices.SpaceChat.List.Data[number]["rooms"][number]["notificationType"];
	spaceID: string;
	isOnlyAdmin: boolean;
	users?: WebServices.SpaceChat.List.Data[number]["rooms"][number]["users"];
	variant?: ChatUI.Size;
}

export default function SpaceChannelOption({
	roomID,
	roomType,
	notificationType,
	spaceID,
	isOnlyAdmin,
	users,
	variant
}: SpaceChannelOptionInterface) {
	const popover = usePopover();
	const modal = useModal();
	const sidebarActions = useSidebarActions();
	const chatRoomActions = useChatRoomActions();
	const myInfo = usePersistUser("me");
	const toast = useToast();
	const chatRoomListActions = useChatRoomListActions();
	const { confirm } = useConfirm();
	const voiceCallAPI = useVoiceCall();
	const { data: notificationSetting } = useNotificationConfig();
	const spaceInfo = useSpaceChannelState(spaceID);
	const { miniChatPopupActions } = useMiniChatPopupManager();
	// const chatAPI = useChat();

	const currentRoom = spaceInfo?.rooms.find((room) => room.roomId === roomID);
	const ownerOfRoom = currentRoom?.ownerId === Configure.userID;

	useEffect(() => {
		if (currentRoom && currentRoom.deleted && currentRoom.roomJoined) {
			popover.hide(GROUP_POPOVER_ID.SPACE_CHAT_SUB_MENU);
			popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);
		}
	}, [currentRoom]);

	const handlePinChatChannel = useCallback(async () => {
		await chatRoomActions.common.pin({ roomID, pin: !currentRoom?.pinned });
		chatRoomListActions.load("Space");
	}, [currentRoom, roomID, spaceID]);

	const channelUserList = currentRoom?.users.map((user) => ({
		name: user.name,
		src: user.profileImagePath,
		userID: user.id
	}));

	const { data: voiceChatjoinedResult } = useQuery({
		queryKey: ["voicecall:checkJoined"],
		queryFn: async () => (await voiceCallAPI.checkJoined.execute()).data,
		refetchOnMount: true,
		refetchOnWindowFocus: false
	});

	const handleClick = async (evt: React.MouseEvent<HTMLElement>) => {
		evt.stopPropagation();
		const target = evt.currentTarget as HTMLElement;
		const { id } = target;

		switch (id) {
			case "vote": {
				chatRoomActions.common.open({ channelID: roomID, type: "Space", spaceID: spaceID });
				modal.show({
					id: ModalID.CHAT_VOTE,
					content: <ChatVote roomID={roomID} />,
					zIndex: ZIndex.SIDEBAR + 1
				});
				popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);
				break;
			}
			case "alarm": {
				if (!notificationSetting?.useSpaceChatNotification) return;
				popover.toggle({
					popoverID: GROUP_POPOVER_ID.CHAT_OPTION,
					uniqueID: "space_alarm",
					content: (
						<ChatAlarmSetting
							isGeneral={roomType === "general"}
							roomID={roomID}
							currentOption={notificationType}
							popoverID={GROUP_POPOVER_ID.CHAT_OPTION}
						/>
					),
					anchorEl: target,
					direction: {
						x: "right",
						y: "top"
					},
					position: {
						x: 60,
						y: 0
					},
					zIndex: ZIndex.POPUP + 2
				});
				break;
			}
			case "member": {
				popover.hide(GROUP_POPOVER_ID.SPACE_CHAT_SUB_MENU);
				chatRoomActions.common.open({ channelID: roomID, type: "Space", spaceID: spaceID! });
				modal.show({
					id: ModalID.SHOW_CHAT_MEMBER,
					content: (
						<ShowChatMember
							spaceID={spaceID}
							channelID={roomID}
							onHide={() => {
								modal.hide(ModalID.SHOW_CHAT_MEMBER);
							}}
							chatKind="Space"
						/>
					)
				});
				break;
			}
			case "channelName": {
				const currentChatFocus = miniChatPopupActions.getCurrentFocus();

				const isFullChatOpened = sidebarActions.isOpen(SidebarID.FullSizeChat);
				const isMiniChatOpened = currentChatFocus?.id === roomID;

				if (isFullChatOpened || isMiniChatOpened) {
					EventHandler.emit(EventID.UPDATE_CHANNEL_NAME, roomID);
				} else {
					chatRoomActions.common.open({ channelID: roomID, spaceID: spaceID, type: "Space" });
					setTimeout(() => {
						EventHandler.emit(EventID.UPDATE_CHANNEL_NAME, roomID);
					}, 500);
				}

				popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);

				break;
			}

			case "create": {
				modal.show({
					id: ModalID.CREATE_SPACE_CHANNEL,
					content: (
						<CreateSpaceChannel
							spaceID={spaceID || ""}
							channelUserList={channelUserList || []}
							onHide={() => {
								modal.hide(ModalID.CREATE_SPACE_CHANNEL);
							}}
						/>
					)
				});

				break;
			}

			case "exit": {
				const getTextFollowByRule = (isCreator: boolean, roomName?: string) =>
					isCreator
						? _P("{{name}} 다른 채널 관리자를 지정 하거나 채널 삭제 버튼으로 삭제 가능합니다.", {
								name: roomName
							})
						: _P("{{name}}채널을 나가면 더이상 채널 이용이 불가합니다. 그래도 나가시겠습니까?", {
								name: roomName
							});

				const getButtonTexts = (isCreator: boolean) => ({
					confirmText: isCreator ? _T("채널 삭제") : _T("확인"),
					cancelText: isCreator ? _T("채널 관리자 이관") : _T("취소")
				});
				const textFollowByRule = getTextFollowByRule(ownerOfRoom, currentRoom?.roomName);
				const { confirmText, cancelText } = getButtonTexts(ownerOfRoom);

				confirm(
					{
						title: textFollowByRule,
						confirmText,
						cancelText
					},
					{
						onConfirm() {
							if (ownerOfRoom) {
								chatRoomActions.spaceChat.remove(spaceID, roomID, {
									onSuccess() {
										chatRoomActions.common.open({
											spaceID,
											channelID: roomID,
											type: "Space"
										});
									},
									onError(error) {
										if (error.code === "48003") {
											toast.show({
												type: "system",
												message: _T("이 채널은 삭제되었습니다.")
											});
										} else {
											console.error("Unexpected error:", error);
										}
									}
								});
								popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);
							} else {
								chatRoomActions.spaceChat.exit(roomID, Properties.browserTabID, {
									onError(error) {
										if (error.code === "48003") {
											toast.show({
												type: "system",
												message: _T("이 채널은 삭제되었습니다.")
											});
										}
									},
									onSuccess() {
										if (currentRoom?.users.length === 0) {
											chatRoomActions.spaceChat.remove(spaceID, roomID, {
												onSuccess() {
													const currentSpace = chatRoomListActions.getSpaceChannel(spaceID);
													chatRoomActions.common.open({
														spaceID: spaceID,
														channelID: currentSpace?.rooms[0].roomId || "",
														type: "Space"
													});
												},
												onError(error) {
													if (error.code === "48003") {
														toast.show({
															type: "system",
															message: _T("이 채널은 삭제되었습니다.")
														});
													}
												}
											});
										}
										chatRoomActions.common.open({
											channelID: roomID,
											type: "Space",
											spaceID: spaceID!
										});
										popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);
									}
								});
							}
						},
						onCancel() {
							if (ownerOfRoom) {
								modal.show({
									id: ModalID.SHOW_CHAT_MEMBER,
									content: (
										<ShowChatMember
											spaceID={spaceID}
											channelID={roomID}
											onHide={() => {
												modal.hide(ModalID.SHOW_CHAT_MEMBER);
											}}
											chatKind="Space"
										/>
									)
								});
							} else {
								modal.hide(ModalID.CONFIRM_POPUP);
							}
						}
					}
				);
				break;
			}

			case "remove": {
				const getTextFollowByRule = (isCreator: boolean, roomName?: string) =>
					_T("{{name}} 채널을 삭제하면 더이상 채널 이용이 불가합니다. 그래도 삭제하시겠습니까?", {
						name: isCreator ? roomName : ""
					});

				const getButtonTexts = () => ({
					confirmText: _T("확인"),
					cancelText: _T("취소")
				});
				const textFollowByRule = getTextFollowByRule(ownerOfRoom, currentRoom?.roomName);
				const { confirmText, cancelText } = getButtonTexts();

				confirm(
					{
						title: textFollowByRule,
						confirmText,
						cancelText
					},
					{
						onConfirm() {
							chatRoomActions.spaceChat.remove(spaceID, roomID, {
								onError(error) {
									if (error.code === "48003") {
										toast.show({
											type: "system",
											message: _T("이 채널은 삭제되었습니다.")
										});
									} else {
										console.error("Unexpected error:", error);
									}
								}
							});
							popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);
						},
						onCancel() {
							modal.hide(ModalID.CONFIRM_POPUP);
						}
					}
				);
				break;
			}

			case "voicecall": {
				if (voiceChatjoinedResult?.joined) {
					toast.show({
						type: "system",
						message: _T("이미 진행중인 보이스챗이 있습니다. 종료하고 진행해주세요.")
					});
				} else {
					await chatRoomActions.common.open({ type: "Space", spaceID: spaceID, channelID: roomID });
					timer(100).subscribe(() => EventHandler.fire("$VoiceCall::Starting$", roomID));
				}
				popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);
				break;
			}

			case "pin": {
				handlePinChatChannel();
				break;
			}
		}
		if (id !== "alarm") {
			popover.hide(GROUP_POPOVER_ID.SPACE_CHAT_SUB_MENU);
		}
	};

	return (
		<S.StyledMenu isGroup={variant === "miniPopup"} fullWidth={variant === "miniPopup"}>
			{currentRoom?.spaceType === "NORMAL" && variant === ("miniPopup" as ChatUI.Size) && (
				<>
					<Menu.List>
						<Menu.Item id="create" onClick={handleClick}>
							<Row style={{ width: "192px" }} verticalAlign="center" horizonAlign="distribute">
								<div>{_T("채널 만들기")}</div>
								<Icon name="Add24" size={16} />
							</Row>
						</Menu.Item>
						{spaceInfo?.rooms.map((room) => (
							<Menu.Item
								key={room.roomId}
								onClick={() => {
									chatRoomActions.common.open({
										type: "Space",
										channelID: room.roomId,
										spaceID: room.spaceId || ""
									});
									popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);
								}}
								focus={room.roomId === roomID}
							>
								<ChannelNameWrapper horizonAlign="left" gap={8}>
									<TypographyTooltip.p
										tooltipProps={{ hasIcon: false }}
										componentProps={{
											maxLine: 1,
											ellipsis: true,
											style: {
												textAlign: "left",
												flex: 1
											},
											stretch: true,
											typoSize: "Body3_MD"
										}}
									>
										{room?.roomName}
									</TypographyTooltip.p>
									<Maybe test={room?.private}>
										<Icon size={16} css={{ minWidth: 16 }} name="Lock24" />
									</Maybe>
									{roomID !== room.roomId && (
										<Icon
											className="new-window"
											name="NewWindow24"
											size={16}
											css={{ minWidth: 16 }}
										/>
									)}
								</ChannelNameWrapper>
							</Menu.Item>
						))}
						{}
					</Menu.List>
					<Divider type="line" />
				</>
			)}
			<Menu.List>
				<Maybe test={!(Boolean(variant) && (spaceInfo?.rooms.length ?? 0) <= 1)}>
					<Menu.Item onClick={handleClick} id="pin">
						{currentRoom?.pinned ? _T("고정해제하기") : _T("고정하기")}
					</Menu.Item>
				</Maybe>
				<ChatAlarmSettingMenu
					notificationType={notificationType}
					onClick={handleClick}
					type={roomType === "general" ? "general" : "other"}
					disabledAlarm={!notificationSetting?.useSpaceChatNotification}
				/>
				<Menu.Item onClick={handleClick} id="voicecall" typoSize="Body3_MD" disabled={currentRoom?.inVoiceChat}>
					{_T("보이스챗")}
				</Menu.Item>
				<Menu.Item onClick={handleClick} id="vote" typoSize="Body3_MD" disabled={currentRoom?.inVoiceChat}>
					{_T("투표하기")}
				</Menu.Item>
				<SwitchCase
					condition={roomType}
					cases={{
						general: (
							<>
								<Maybe test={users?.map((user) => user.id).includes(myInfo?.id || "")}>
									<Menu.Item id="member" onClick={handleClick}>
										{_T("채널 멤버 보기")}
									</Menu.Item>
								</Maybe>
							</>
						)
					}}
					defaultCase={
						<>
							<Maybe test={users?.map((user) => user.id).includes(myInfo?.id || "")}>
								<Menu.Item id="member" onClick={handleClick}>
									{_T("채널 멤버 보기")}
								</Menu.Item>
							</Maybe>
							<Maybe test={spaceInfo?.spaceAuthority === "SPACE_ADMIN" || ownerOfRoom}>
								<Menu.Item id="channelName" onClick={handleClick}>
									{_T("채널명 바꾸기")}
								</Menu.Item>
							</Maybe>
							<Maybe
								test={
									(roomType === "private" && ownerOfRoom && !isOnlyAdmin) ||
									(roomType === "private" && spaceInfo?.spaceAuthority === "SPACE_USER")
								}
							>
								<Divider type="line" />
							</Maybe>
							{roomType === "private" ? (
								<Menu.Item id="exit" onClick={handleClick}>
									<Typography typoColor="warning_80_100" typoSize="Body3_MD">
										{_T("채널 나가기")}
									</Typography>
								</Menu.Item>
							) : null}
							<Maybe test={spaceInfo?.spaceAuthority === "SPACE_ADMIN" || ownerOfRoom}>
								<Menu.Item id="remove" onClick={handleClick}>
									<Typography typoColor="warning_80_100" typoSize="Body3_MD">
										{_T("채널 삭제하기")}
									</Typography>
								</Menu.Item>
							</Maybe>
						</>
					}
				/>
			</Menu.List>
		</S.StyledMenu>
	);
}

const ChannelNameWrapper = styled(Row)`
	width: 192px;
	.new-window {
		display: none;
	}
	&:hover {
		.new-window {
			display: flex;
		}
	}
`;
